import { useState } from 'react';
import type {
  CampaignSetup,
  Commercials,
  ConfigResponse,
  IntegrationDetails,
  KeyMetric,
  RecordDetails,
  RolloutDraft,
} from '../../types';
import {
  AddButton,
  cx,
  Field,
  FieldGrid,
  Input,
  InputWithUnit,
  PrefixInput,
  ReadonlyValue,
  RemoveButton,
  Section,
  Segmented,
  Select,
  SourceChip,
  TextArea,
} from '../ui/primitives';
import { formatCount, newMetric, type IssueMap, errorFor } from '../../state/draft';

/**
 * The Review & Edit form sections, laid out to match the supplied screenshots.
 * Each section is a controlled component: it renders the slice it is given and
 * reports changes upward, so all draft mutation stays in the screen.
 */

type Sources = RolloutDraft['sources'];

function chip(sources: Sources, path: string) {
  return <SourceChip source={sources[path]} />;
}

// ---------------------------------------------------------------------------
// Record Details
// ---------------------------------------------------------------------------

export function RecordDetailsSection({
  record,
  businessType,
  statusOptions,
  sources,
  issues,
  onChange,
}: {
  record: RecordDetails;
  businessType: RolloutDraft['businessType'];
  statusOptions: string[];
  sources: Sources;
  issues: IssueMap;
  onChange: (patch: Partial<RecordDetails>, paths: string[]) => void;
}) {
  return (
    <Section title="Record Details">
      <FieldGrid>
        <Field label="Collab ID" htmlFor="collab-id" source={chip(sources, 'record.collabId')}>
          <Input
            id="collab-id"
            value={record.collabId}
            placeholder="e.g. 120826001"
            onChange={(event) => onChange({ collabId: event.target.value }, ['record.collabId'])}
          />
        </Field>

        <Field
          label="Brand Name"
          htmlFor="brand-name"
          required
          error={errorFor(issues, 'record.brandName')}
          source={chip(sources, 'record.brandName')}
        >
          <Input
            id="brand-name"
            value={record.brandName}
            placeholder="e.g. Dominoes"
            invalid={Boolean(errorFor(issues, 'record.brandName'))}
            onChange={(event) => onChange({ brandName: event.target.value }, ['record.brandName'])}
          />
        </Field>

        {/* Only the agency workflow has an agency, per the required-field rules. */}
        {businessType === 'AGENCY' ? (
          <>
            <Field
              label="Agency Name"
              htmlFor="agency-name"
              required
              error={errorFor(issues, 'record.agencyName')}
              source={chip(sources, 'record.agencyName')}
              help="Names the Drive folder and the document."
            >
              <Input
                id="agency-name"
                value={record.agencyName}
                placeholder="e.g. IA"
                invalid={Boolean(errorFor(issues, 'record.agencyName'))}
                onChange={(event) =>
                  onChange({ agencyName: event.target.value }, ['record.agencyName'])
                }
              />
            </Field>

            {/*
              Optional, and deliberately not part of Drive naming: every
              sub-agency of one agency files into the same folder rather than
              scattering into near-duplicate ones.
            */}
            <Field
              label="Sub Agency"
              htmlFor="sub-agency-name"
              source={chip(sources, 'record.subAgencyName')}
              help="Shown in the document as Agency - Sub Agency. Does not affect folder or file names."
            >
              <Input
                id="sub-agency-name"
                value={record.subAgencyName}
                placeholder="e.g. Innocean Media"
                onChange={(event) =>
                  onChange({ subAgencyName: event.target.value }, ['record.subAgencyName'])
                }
              />
            </Field>
          </>
        ) : (
          <Field label="Business Type" htmlFor="business-type-display">
            <ReadonlyValue value="Direct Brand" />
          </Field>
        )}

        <Field label="Status" htmlFor="status" source={chip(sources, 'record.status')}>
          <Select
            id="status"
            value={record.status}
            placeholder="Select Status"
            options={statusOptions.map((option) => ({ value: option, label: option }))}
            onChange={(event) => onChange({ status: event.target.value }, ['record.status'])}
          />
        </Field>
      </FieldGrid>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Set Type
// ---------------------------------------------------------------------------

/**
 * Set Type offers its two values and still accepts anything typed.
 *
 * A plain `<select>` cannot do both, so the two values are offered as buttons
 * with the current value in an ordinary text box beneath them. That keeps the
 * common case to one click while leaving the field genuinely editable — a brief
 * occasionally words this a third way, and that wording has to survive.
 *
 * The per-cohort option names the number of cohorts currently selected, so what
 * the button writes is always what the document will say.
 */
export function SetTypeField({
  setType,
  options,
  issues,
  onChange,
}: {
  setType: string;
  /** The two suggested values, already resolved against the cohort count. */
  options: string[];
  issues: IssueMap;
  onChange: (value: string) => void;
}) {
  return (
    <Field
      label="Set Type"
      htmlFor="set-type"
      required
      error={errorFor(issues, 'setType')}
      help="Pick one, or type your own wording."
      className="max-w-xl"
    >
      <div className="space-y-2.5">
        <div className="flex flex-wrap gap-2">
          {options.map((option) => {
            const selected = option === setType;
            return (
              <button
                key={option}
                type="button"
                aria-pressed={selected}
                onClick={() => onChange(option)}
                className={cx(
                  'rounded-input border px-3 py-2 text-left text-micro transition-colors',
                  'focus-visible:ring-2 focus-visible:ring-accent/40',
                  selected
                    ? 'border-accent bg-accent-soft text-ink'
                    : 'border-line bg-surface text-ink-muted hover:border-line-strong',
                )}
              >
                {option}
              </button>
            );
          })}
        </div>

        <Input
          id="set-type"
          value={setType}
          placeholder="e.g. 1 Set (Combined Audience)"
          invalid={Boolean(errorFor(issues, 'setType'))}
          onChange={(event) => onChange(event.target.value)}
        />
      </div>
    </Field>
  );
}

// ---------------------------------------------------------------------------
// Campaign Setup
// ---------------------------------------------------------------------------

export function CampaignSetupSection({
  campaign,
  sources,
  issues,
  /** Derived from start date + duration; shown in the disabled end-date field. */
  derivedEndDate,
  onChange,
}: {
  campaign: CampaignSetup;
  sources: Sources;
  issues: IssueMap;
  derivedEndDate: string;
  onChange: (patch: Partial<CampaignSetup>, paths: string[]) => void;
}) {
  return (
    <Section title="Campaign Setup">
      <Field
        label="Duration of Campaign"
        htmlFor="duration"
        error={errorFor(issues, 'campaign.durationValue')}
        source={chip(sources, 'campaign.durationValue')}
        className="max-w-md"
      >
        <InputWithUnit
          id="duration"
          type="number"
          min={1}
          value={campaign.durationValue}
          placeholder="Enter Number"
          unitLabel="Duration unit"
          unitValue={campaign.durationUnit}
          invalid={Boolean(errorFor(issues, 'campaign.durationValue'))}
          unitOptions={[
            { value: 'DAYS', label: 'Days' },
            { value: 'WEEKS', label: 'Weeks' },
            { value: 'MONTHS', label: 'Months' },
          ]}
          onUnitChange={(value) =>
            onChange({ durationUnit: value as CampaignSetup['durationUnit'] }, [
              'campaign.durationValue',
            ])
          }
          onChange={(event) =>
            onChange({ durationValue: event.target.value }, ['campaign.durationValue'])
          }
        />
      </Field>

      <FieldGrid>
        <Field
          label="Likely Start Date"
          htmlFor="start-date"
          source={chip(sources, 'campaign.likelyStartDate')}
        >
          <Input
            id="start-date"
            type="date"
            className="date-accent"
            value={campaign.likelyStartDate}
            onChange={(event) =>
              onChange({ likelyStartDate: event.target.value }, ['campaign.likelyStartDate'])
            }
          />
        </Field>

        {/*
          The design shows Likely End Date greyed out: it is computed from the
          start date and duration. It stays read-only so the two can never drift.
        */}
        <Field
          label="Likely End Date"
          htmlFor="end-date"
          muted
          help={
            derivedEndDate
              ? 'Calculated from the start date and duration.'
              : 'Set a start date and duration to calculate this.'
          }
        >
          <Input
            id="end-date"
            type="date"
            disabled
            readOnly
            value={derivedEndDate}
            className="date-accent"
          />
        </Field>

        <Field
          label="Objective of Campaign"
          htmlFor="objective"
          source={chip(sources, 'campaign.objective')}
        >
          <TextArea
            id="objective"
            rows={3}
            value={campaign.objective}
            placeholder="e.g. Drive credit card applications from affluent salaried users in metro cities."
            onChange={(event) => onChange({ objective: event.target.value }, ['campaign.objective'])}
          />
        </Field>

        <Field
          label="Additional Info"
          htmlFor="additional-info"
          hint="(optional)"
          source={chip(sources, 'campaign.additionalInfo')}
        >
          <TextArea
            id="additional-info"
            rows={3}
            value={campaign.additionalInfo}
            placeholder="e.g. Creative notes, exclusions, frequency caps, anything the ops team should know."
            onChange={(event) =>
              onChange({ additionalInfo: event.target.value }, ['campaign.additionalInfo'])
            }
          />
        </Field>
      </FieldGrid>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Commercials
// ---------------------------------------------------------------------------

export function CommercialsSection({
  commercials,
  currencySymbol,
  receiptMethods,
  sources,
  issues,
  computedImpressions,
  onChange,
}: {
  commercials: Commercials;
  currencySymbol: string;
  receiptMethods: string[];
  sources: Sources;
  issues: IssueMap;
  computedImpressions: string;
  onChange: (patch: Partial<Commercials>, paths: string[]) => void;
}) {
  const isAuto = commercials.impressionMode === 'AUTO';
  const displayed = isAuto ? computedImpressions : commercials.impression;

  return (
    <Section title="Commercials">
      <FieldGrid>
        <Field label="RO Number" htmlFor="ro-number" source={chip(sources, 'commercials.roNumber')}>
          <Input
            id="ro-number"
            value={commercials.roNumber}
            placeholder="Enter RO Number"
            onChange={(event) =>
              onChange({ roNumber: event.target.value }, ['commercials.roNumber'])
            }
          />
        </Field>

        <Field
          label={`RO Value (${currencySymbol})`}
          htmlFor="ro-value"
          error={errorFor(issues, 'commercials.roValue')}
          source={chip(sources, 'commercials.roValue')}
        >
          <PrefixInput
            id="ro-value"
            prefix={currencySymbol}
            type="number"
            min={0}
            value={commercials.roValue}
            placeholder="Enter Amount"
            invalid={Boolean(errorFor(issues, 'commercials.roValue'))}
            onChange={(event) => onChange({ roValue: event.target.value }, ['commercials.roValue'])}
          />
        </Field>

        <Field
          label={`CPM (${currencySymbol})`}
          htmlFor="cpm"
          error={errorFor(issues, 'commercials.cpm')}
          source={chip(sources, 'commercials.cpm')}
        >
          <PrefixInput
            id="cpm"
            prefix={currencySymbol}
            type="number"
            min={0}
            step="0.01"
            value={commercials.cpm}
            placeholder="Enter Amount"
            invalid={Boolean(errorFor(issues, 'commercials.cpm'))}
            onChange={(event) => onChange({ cpm: event.target.value }, ['commercials.cpm'])}
          />
        </Field>

        <Field
          label="Impression"
          htmlFor="impression"
          error={errorFor(issues, 'commercials.impression')}
          help={
            isAuto
              ? `(RO Value ÷ CPM) × 1,000${displayed ? ` = ${formatCount(displayed)}` : ''}`
              : 'Entered manually.'
          }
        >
          <div className="flex gap-2">
            {isAuto ? (
              <ReadonlyValue value={displayed ? formatCount(displayed) : ''} />
            ) : (
              <Input
                id="impression"
                type="number"
                min={0}
                value={commercials.impression}
                placeholder="Enter Impressions"
                invalid={Boolean(errorFor(issues, 'commercials.impression'))}
                onChange={(event) =>
                  onChange({ impression: event.target.value }, ['commercials.impression'])
                }
              />
            )}
            <Segmented
              label="Impression mode"
              value={commercials.impressionMode}
              options={[
                { value: 'AUTO', label: 'Auto' },
                { value: 'MANUAL', label: 'Manual' },
              ]}
              onChange={(mode) =>
                onChange(
                  {
                    impressionMode: mode,
                    // Switching to Manual seeds the field with the calculated
                    // figure so the user edits a number rather than a blank.
                    impression: mode === 'MANUAL' ? commercials.impression || computedImpressions : '',
                  },
                  ['commercials.impression'],
                )
              }
            />
          </div>
        </Field>

        <Field
          label="Impression Data Receipt Method"
          htmlFor="receipt-method"
          source={chip(sources, 'commercials.impressionReceiptMethod')}
        >
          <Select
            id="receipt-method"
            value={commercials.impressionReceiptMethod}
            placeholder="Select Method"
            options={receiptMethods.map((option) => ({ value: option, label: option }))}
            onChange={(event) =>
              onChange({ impressionReceiptMethod: event.target.value }, [
                'commercials.impressionReceiptMethod',
              ])
            }
          />
        </Field>
      </FieldGrid>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Other Key Metrics & Benchmarks
// ---------------------------------------------------------------------------

export function MetricsSection({
  metrics,
  currencySymbol,
  issues,
  onChange,
}: {
  metrics: KeyMetric[];
  currencySymbol: string;
  issues: IssueMap;
  onChange: (metrics: KeyMetric[]) => void;
}) {
  const units = [currencySymbol, '%', 'x', 'sec', 'none'];

  const patchAt = (index: number, patch: Partial<KeyMetric>) => {
    onChange(metrics.map((metric, position) => (position === index ? { ...metric, ...patch } : metric)));
  };

  return (
    <Section title="Other Key Metrics &amp; Benchmarks" hint="(Optional)">
      {metrics.length ? (
        <div className="space-y-row">
          {metrics.map((metric, index) => (
            <div key={metric.id} className="flex items-start gap-3">
              <FieldGrid className="flex-1">
                <Field label="Metric" htmlFor={`metric-${metric.id}`}>
                  <Input
                    id={`metric-${metric.id}`}
                    value={metric.metric}
                    placeholder="e.g. CTR"
                    invalid={Boolean(errorFor(issues, `metrics.${index}`))}
                    onChange={(event) => patchAt(index, { metric: event.target.value })}
                  />
                </Field>

                <Field
                  label="Benchmark"
                  htmlFor={`benchmark-${metric.id}`}
                  error={errorFor(issues, `metrics.${index}`)}
                >
                  <div
                    className={`flex h-12 overflow-hidden rounded-input border ${
                      errorFor(issues, `metrics.${index}`) ? 'border-danger' : 'border-line'
                    } bg-surface focus-within:border-accent focus-within:ring-2 focus-within:ring-accent/25`}
                  >
                    <input
                      id={`benchmark-${metric.id}`}
                      value={metric.benchmark}
                      placeholder="E.g. 0.8"
                      onChange={(event) => patchAt(index, { benchmark: event.target.value })}
                      className="min-w-0 flex-1 bg-transparent px-4 text-field text-ink placeholder:text-ink-faint focus:outline-none"
                    />
                    <div className="relative shrink-0">
                      <select
                        aria-label={`Unit for ${metric.metric || `metric ${index + 1}`}`}
                        value={metric.unit}
                        onChange={(event) => patchAt(index, { unit: event.target.value })}
                        className="h-full cursor-pointer appearance-none border-l border-line bg-surface-muted pl-4 pr-9 text-field text-ink-muted focus:outline-none"
                      >
                        {units.map((unit) => (
                          <option key={unit} value={unit === 'none' ? '' : unit}>
                            {unit}
                          </option>
                        ))}
                      </select>
                      <svg
                        viewBox="0 0 20 20"
                        aria-hidden="true"
                        className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-accent"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.75"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M5 7.5 10 12.5 15 7.5" />
                      </svg>
                    </div>
                  </div>
                </Field>
              </FieldGrid>

              <div className="pt-7">
                <RemoveButton
                  onClick={() => onChange(metrics.filter((_, position) => position !== index))}
                  label={`Remove metric ${index + 1}`}
                />
              </div>
            </div>
          ))}
        </div>
      ) : null}

      <AddButton onClick={() => onChange([...metrics, newMetric(currencySymbol)])}>
        Add Metric
      </AddButton>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Integration Details
// ---------------------------------------------------------------------------

export function IntegrationSection({
  integration,
  config,
  sources,
  issues,
  onChange,
}: {
  integration: IntegrationDetails;
  config: ConfigResponse;
  sources: Sources;
  issues: IssueMap;
  onChange: (patch: Partial<IntegrationDetails>, paths: string[]) => void;
}) {
  // Meta needs App ID / Secret / Token; Google Ads and DV360 need only the
  // account id. Driven by configuration so a new channel needs no code change.
  const needsCredentials = config.channelsWithApiCredentials.includes(
    integration.channel.toUpperCase(),
  );

  return (
    <Section title="Integration Details">
      <FieldGrid>
        <Field
          label="Channel"
          htmlFor="channel"
          required
          error={errorFor(issues, 'integration.channel')}
          source={chip(sources, 'integration.channel')}
        >
          <Select
            id="channel"
            value={integration.channel}
            placeholder="Select Channel"
            invalid={Boolean(errorFor(issues, 'integration.channel'))}
            options={config.channels.map((channel) => ({
              value: channel,
              label: channelLabel(channel),
            }))}
            onChange={(event) =>
              onChange({ channel: event.target.value }, ['integration.channel'])
            }
          />
        </Field>

        <Field
          label="Ad Account ID"
          htmlFor="ad-account-id"
          source={chip(sources, 'integration.adAccountId')}
        >
          <Input
            id="ad-account-id"
            value={integration.adAccountId}
            placeholder="E.g. 120-264-3834"
            onChange={(event) =>
              onChange({ adAccountId: event.target.value }, ['integration.adAccountId'])
            }
          />
        </Field>
      </FieldGrid>

      {needsCredentials ? (
        <div className="space-y-4">
          <p className="text-label text-ink-muted">
            via API &mdash; Additional Details
          </p>

          <div className="grid grid-cols-1 gap-x-8 gap-y-row sm:grid-cols-3">
            <Field label="App ID" htmlFor="app-id" error={errorFor(issues, 'integration.appId')}>
              <Input
                id="app-id"
                value={integration.appId}
                placeholder="E.g. 120-264-3834"
                autoComplete="off"
                onChange={(event) => onChange({ appId: event.target.value }, [])}
              />
            </Field>

            <Field label="App Secret" htmlFor="app-secret">
              <SecretInput
                id="app-secret"
                value={integration.appSecret}
                onChange={(value) => onChange({ appSecret: value }, [])}
              />
            </Field>

            <Field label="Access Token" htmlFor="access-token">
              <SecretInput
                id="access-token"
                value={integration.accessToken}
                onChange={(value) => onChange({ accessToken: value }, [])}
              />
            </Field>
          </div>

          <p className="text-micro text-ink-faint">
            These credentials are used only to reach the channel. They are never written into the
            Google Document and are not stored.
          </p>
        </div>
      ) : null}
    </Section>
  );
}

/** Human-friendly channel names for the values stored in uppercase. */
function channelLabel(channel: string): string {
  const labels: Record<string, string> = {
    META: 'Meta',
    GOOGLE: 'Google Ads',
    DV360: 'DV360',
    GAM: 'GAM',
    OTHER: 'Other',
  };
  return labels[channel] ?? channel;
}

/** Masked input with a SHOW toggle, as shown on the Meta variant of the design. */
function SecretInput({
  id,
  value,
  onChange,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const [revealed, setRevealed] = useState(false);

  return (
    <div className="flex h-12 overflow-hidden rounded-input border border-line bg-surface focus-within:border-accent focus-within:ring-2 focus-within:ring-accent/25">
      <input
        id={id}
        type={revealed ? 'text' : 'password'}
        value={value}
        autoComplete="off"
        placeholder="************"
        onChange={(event) => onChange(event.target.value)}
        className="min-w-0 flex-1 bg-transparent px-4 text-field text-ink placeholder:text-ink-faint focus:outline-none"
      />
      <button
        type="button"
        aria-pressed={revealed}
        onClick={() => setRevealed((current) => !current)}
        className="shrink-0 px-4 text-micro font-semibold tracking-wide text-ink-muted hover:text-ink"
      >
        {revealed ? 'HIDE' : 'SHOW'}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Performance (disabled placeholder, as in the design)
// ---------------------------------------------------------------------------

export function PerformanceSection() {
  return (
    <Section title="Performance" muted>
      <Field label="Columns" htmlFor="performance-columns" muted className="max-w-sm">
        <Select
          id="performance-columns"
          value=""
          disabled
          placeholder="Select Columns"
          options={[]}
          onChange={() => undefined}
        />
      </Field>
      <p className="text-micro text-ink-faint">
        Performance columns are configured after the campaign goes live.
      </p>
    </Section>
  );
}
